import clsx from 'clsx';
import { memo, MouseEvent, ReactNode, useMemo } from 'react';
import { Link, To } from 'react-router-dom';
import { MarkInProgressIcon } from 'src/modules/common/icons/MarkInProgressIcon.tsx';
import { MarkEmptyIcon } from 'src/modules/common/icons/MarkEmptyIcon.tsx';
import { MarkDoneIcon } from 'src/modules/common/icons/MarkDoneIcon.tsx';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { Tooltip } from 'src/modules/common/components/Tooltip/Tooltip.tsx';
import { CrossIcon } from 'src/modules/common/icons/CrossIcon.tsx';

export type BreadcrumbProps = {
    title: string;
    value: ReactNode;
    tooltipValue?: ReactNode;
    selected?: boolean;
    to: To;
    state?: Record<string, unknown>;
    actionShown?: boolean;
    disabled?: boolean;
    onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
    onActionClick?: (e: MouseEvent<HTMLDivElement>) => void;
}

export const Breadcrumb = memo(({
    to,
    state,
    title,
    value,
    tooltipValue,
    selected,
    actionShown = true,
    disabled = false,
    onClick,
    onActionClick,
}: BreadcrumbProps) => {
    const stateIcon = useMemo(() => {
        if (selected) {
            return <MarkInProgressIcon/>;
        }

        if (disabled) {
            return <MarkEmptyIcon/>;
        }

        return <MarkDoneIcon/>;
    }, [selected, disabled]);

    const handleActionClick = (e: MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        onActionClick?.(e);
    };

    const disabledBreadcrumbHandler = (e: MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
    };

    return disabled ? (
        <Link
            className={clsx({
                'vss-breadcrumb': true,
                'vss-breadcrumb--selected': selected,
                'vss-breadcrumb--disabled': disabled,
            })}
            onClick={disabled ? disabledBreadcrumbHandler : onClick}
            to={to}
            state={state}
        >
            <div className={clsx({
                'vss-breadcrumb__status-mark': true,
                'vss-breadcrumb__status-mark--empty': !selected && disabled,
            })}
            >
                {stateIcon}
            </div>

            <div className="vss-breadcrumb__contents">
                <p
                    className={clsx({
                        'vss-breadcrumb__title': true,
                        'vss-breadcrumb__title--limited': selected && actionShown,
                    })}
                ><I18n messageId={title}/>
                </p>

                <p className="vss-breadcrumb__value">{value}</p>

                {selected && actionShown && (
                    <div
                        data-testid="breadcrumb-action"
                        role="button"
                        tabIndex={0}
                        onKeyDown={() => null}
                        className="vss-breadcrumb__action"
                        onClick={handleActionClick}
                    >
                        <CrossIcon/>
                    </div>
                )}
            </div>
        </Link>
    ) : (
        <Tooltip title={<I18n messageId={title}/>} value={tooltipValue || value} disabled={disabled}>
            <Link
                className={clsx({
                    'vss-breadcrumb': true,
                    'vss-breadcrumb--selected': selected,
                    'vss-breadcrumb--disabled': disabled,
                })}
                onClick={disabled ? disabledBreadcrumbHandler : onClick}
                to={to}
                state={state}
            >
                <div className={clsx({
                    'vss-breadcrumb__status-mark': true,
                    'vss-breadcrumb__status-mark--empty': !selected && disabled,
                })}
                >
                    {stateIcon}
                </div>

                <div className="vss-breadcrumb__contents">
                    <p
                        className={clsx({
                            'vss-breadcrumb__title': true,
                            'vss-breadcrumb__title--limited': selected && actionShown,
                        })}
                    ><I18n messageId={title}/>
                    </p>

                    <p className="vss-breadcrumb__value">{value}</p>

                    {selected && actionShown && (
                        <div
                            data-testid="breadcrumb-action"
                            role="button"
                            tabIndex={0}
                            onKeyDown={() => null}
                            className="vss-breadcrumb__action"
                            onClick={handleActionClick}
                        >
                            <CrossIcon/>
                        </div>
                    )}
                </div>
            </Link>
        </Tooltip>
    );
});
